import { ScrollArea } from '@neo/primitives/scroll-area'
import { getFirstEventTimestamp } from '../../flow-runs/utils'
import { useFlowStore } from '../../store'
import { useFlowRunStore } from '../store'
import type { NodeChunkCreatedEvent, NodeExecutedEvent, SystemChunkCreatedEvent } from '../types'
import { EventRenderer } from './event-renderer'

type FlattenedEvent = (NodeExecutedEvent | SystemChunkCreatedEvent | NodeChunkCreatedEvent) & {
  nodeChannel: string | null
  isLastChunk: boolean
}

interface EventInterfaceProps {
  shouldShowAudioPlayer?: boolean
  /**
   * Treat no chunk as the newest, so reply buttons are inert. In the live playground the newest
   * chunk's controls publish to the conversation's channel; against a finished run that would
   * send a real message. Defaults false, leaving the playground and legacy run view untouched.
   */
  readOnly?: boolean
}

export const EventInterface = ({
  shouldShowAudioPlayer = false,
  readOnly = false,
}: EventInterfaceProps) => {
  // Use a more specific selector to avoid unnecessary re-renders
  const executionHistory = useFlowRunStore((state) => state.executionHistory)
  const currentFlowType = useFlowStore((state) => state.currentFlowType)

  // Flatten events and chunks into a single array and sort by timestamp
  const flattenedEvents: FlattenedEvent[] = executionHistory
    .flatMap((event) => {
      // Handle direct events without chunks
      if (!event.chunks || event.chunks.length === 0) {
        return [
          {
            ...event,
            nodeChannel: event.node_config?.channel || null,
            isLastChunk: !readOnly,
          } as FlattenedEvent,
        ]
      }
      // Handle events with chunks
      return [
        {
          ...event,
          nodeChannel: event.node_config?.channel || null,
          isLastChunk: false,
        } as FlattenedEvent,
      ].concat(
        event.chunks
          .filter(
            (chunk) =>
              chunk.event_name === 'NODE_CHUNK_CREATED' ||
              chunk.event_name === 'SYSTEM_CHUNK_CREATED',
          )
          .map((chunk, index, arr) => ({
            ...chunk,
            nodeChannel: event.node_config?.channel || null,
            isLastChunk: !readOnly && index === arr.length - 1,
          })),
      ) as FlattenedEvent[]
    })
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp))

  // Early return if no events to render
  if (flattenedEvents.length === 0) {
    return (
      <ScrollArea className="flex-1">
        <div className="flex min-h-32 items-center justify-center h-full px-4 text-center font-inter text-s-400 text-secondary">
          No events to display
        </div>
      </ScrollArea>
    )
  }

  const firstEventTimestamp = shouldShowAudioPlayer
    ? getFirstEventTimestamp(currentFlowType, flattenedEvents)
    : undefined

  return (
    <ScrollArea className="flex-1">
      <div className="flex min-w-0 flex-col gap-4 font-inter text-foreground event-interface">
        {flattenedEvents.map((event) => (
          <EventRenderer
            key={`${event.node_uuid}-${event.timestamp}`}
            event={event}
            firstEventTimestamp={firstEventTimestamp}
            nodeChannel={event.nodeChannel}
            isLastChunk={event.isLastChunk}
          />
        ))}
      </div>
    </ScrollArea>
  )
}
